import { Button, Dropdown } from 'react-bootstrap';
import { FaEye, FaFilePdf, FaDownload, FaEnvelope, FaTrash } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';

// Utilitaires
import { formatAmountInThousands, formatDate } from "../../../../service/utils/formatters";
import { formatAmount, getStatusClass } from '../../../../service/utils/orderUtils';
import { formatCurrency, formatCurrencys } from '../../../../service/utils/facturationUtils';

/**
 * LIGNE DE TABLEAU TRANSACTION - Gère l'affichage et les actions d'une ligne de transaction
 * @param {Object} props
 * @param {Object} props.transaction - Données de la transaction
 * @param {boolean} props.hasNotice - Si un avis d'exécution existe
 * @param {Object} props.notice - Données de l'avis
 * @param {boolean} props.noticeLoading - État de chargement des avis
 * @param {Function} props.onExecutionNotice - Callback pour générer/voir avis
 * @param {Function} props.onDownloadNotice - Callback pour télécharger avis
 * @param {Function} props.onSendNotice - Callback pour envoyer avis par email
 * @param {Function} props.onViewTransaction - Callback pour voir les détails
 * @param {Function} props.onDeleteTransaction - Callback pour supprimer
 */
const TransactionTableRow = ({
  transaction,
  hasNotice,
  notice,
  noticeLoading,
  onExecutionNotice,
  onDownloadNotice,
  onSendNotice,
  onViewTransaction,
  onDeleteTransaction
}) => {
  return (
    <tr className="trans-td-list" key={transaction.id}>
      {/* Colonne Émetteur */}
      <td className="fw-semibold">
        <div className="d-flex align-items-center">
          <div className="avatar-sm bg-primary bg-opacity-10 rounded-circle me-3">
            <span className="avatar-title text-black fw-bold">
              {transaction.fromAccountCompany?.charAt(0)?.toUpperCase() || 'C'}
            </span>
          </div>
          <div>
            <h6 className="mb-0 fw-semibold">{transaction.fromAccountCompany} {transaction.fromAccountEmail}</h6>
            {/* <small className="text-muted">Ref: {facturation.id}</small> */}
          </div>
        </div>
      </td>
      
      {/* Colonne Destinataire */}
      <td className="text-dark text-center">
        {transaction.toAccountEmail} {transaction.toAccountCompany}
      </td>
      
      {/* Colonne Type d'ordre */}
      <td className="text-center">
        <span 
          className={`badge text-center rounded-pill ${
            transaction.side === 'Buy' 
              ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' 
              : 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25'
          }`}
        >
          {transaction.side}
        </span>
      </td>
      
      {/* Colonne Quantité */}
      <td className="text-center fw-medium">{transaction.quantity}</td>
      
      {/* Colonne Prix */}
      <td className="py-2 text text-center"> 
        <span className="counter-amount-badge">
          {formatAmountInThousands(transaction.price)} 
        </span>
      </td>
      
      {/* Colonne Date de création */}
      <td className="text-nowrap text-center">
        {formatDate(transaction.creationDate)}
      </td>
      
      {/* Colonne Statut */}
      <td className='text text-center'>
        <span className={`badge rounded-pill text-center ${getStatusClass(transaction.status)}`}>
          {transaction.status}
        </span>
      </td>
      
      {/* Colonne Avis d'exécution */}
      <td className="text-center">
        {transaction.status === 'Completed' && (
          <div className="btn-group btn-group-sm">
            {/* Bouton principal : Générer/Voir */}
            <Button
              variant={hasNotice ? "outline-info" : "outline-primary"}
              size="sm"
              onClick={() => onExecutionNotice(transaction.id)}
              disabled={noticeLoading}
              title={hasNotice ? "Voir l'avis" : "Générer l'avis"}
              data-tooltip-id={`notice-tooltip-${transaction.id}`}
            >
              {noticeLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              ) : hasNotice ? (
                <FaEye />
              ) : (
                <FaFilePdf />
              )}
            </Button>
            
            {/* Boutons secondaires (seulement si avis existe) */}
            {hasNotice && (
              <>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => onDownloadNotice(transaction.id)}
                  title="Télécharger"
                  data-tooltip-id={`download-tooltip-${transaction.id}`}
                >
                  <FaDownload />
                </Button>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => onSendNotice(transaction.id)}
                  title="Envoyer par email"
                  data-tooltip-id={`email-tooltip-${transaction.id}`}
                >
                  <FaEnvelope />
                </Button>
              </>
            )}
            
            {/* Tooltips pour les boutons d'avis */}
            <Tooltip id={`notice-tooltip-${transaction.id}`} />
            {hasNotice && (
              <>
                <Tooltip id={`download-tooltip-${transaction.id}`} />
                <Tooltip id={`email-tooltip-${transaction.id}`} />
              </>
            )}
          </div>
        )}
      </td>
      
      {/* Colonne Actions */}
      <td className="text-center actions-col">
        <Dropdown align="end">
          <button
            className="btn btn-link p-0 m-0 text-dark"
            style={{ fontSize: "1.5rem", lineHeight: "1", border: "none" }}
            id={`dropdownMenu-${transaction.id}`}
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            ...
          </button>
          <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`dropdownMenu-${transaction.id}`}>
            <li>
              <button 
                className="dropdown-item text-primary" 
                onClick={() => onViewTransaction(transaction.id)}
              >
                <FaEye className="me-1" /> Détails
              </button>
            </li>
            <li>
              <button 
                className="dropdown-item text-danger" 
                onClick={() => onDeleteTransaction(transaction)}
              >
                <FaTrash className="me-1" /> Supprimer
              </button>
            </li>
          </ul>
        </Dropdown>
      </td>
    </tr>
  );
};

export default TransactionTableRow;